/** CDRs page with URL-synced filters (A4), saved views (D3), date presets,
 * destination filter, empty state, bulk actions, and server-side sorting (D2). */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from "@mui/material";
import {
  BookmarkAdd as SaveViewIcon,
  Delete as DeleteViewIcon,
} from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { startOfDay, endOfDay } from "date-fns";
import { cdrsApi, suppliersApi } from "../api/client";
import { Cdr, CdrListResponse } from "../types/cdr";
import { Supplier } from "../types";
import DateFilter from "../components/cdrs/DateFilter";
import QuickDatePresets from "../components/cdrs/QuickDatePresets";
import DestinationFilter from "../components/cdrs/DestinationFilter";
import EmptyState from "../components/cdrs/EmptyState";
import BulkActionBar from "../components/cdrs/BulkActionBar";
import ExportButton from "../components/cdrs/ExportButton";
import CdrBrowser from "../components/cdrs/CdrBrowser";
import CdrDetailDialog from "../components/cdrs/CdrDetailDialog";
import PageHeader from "../components/common/PageHeader";
import { useNotify } from "../components/common/NotificationProvider";
import type { SortingState } from "@tanstack/react-table";

/** LocalStorage key for saved CDR views (D3). */
const SAVED_VIEWS_KEY = "cdrs_saved_views";

interface SavedView {
  name: string;
  start: string;
  end: string;
  carrier: string;
  status: string;
  destination: string;
}

/** Load saved views from localStorage. */
function loadSavedViews(): SavedView[] {
  try {
    const raw = localStorage.getItem(SAVED_VIEWS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Default date range: Today. */
function getDefaultDateRange(): { start: Date; end: Date } {
  return {
    start: startOfDay(new Date()),
    end: endOfDay(new Date()),
  };
}

/** CDRs page component. */
export default function CDRs() {
  const notify = useNotify();
  const [searchParams, setSearchParams] = useSearchParams();

  // ---- A4: filter state initialized from the URL so views are shareable
  // and survive refresh/back-navigation -------------------------------
  const [cdrs, setCdrs] = useState<Cdr[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const [datePreset, setDatePreset] = useState(() => searchParams.get("preset") || "today");
  const [startDate, setStartDate] = useState<Date>(
    () => (searchParams.get("start") ? new Date(searchParams.get("start") as string) : getDefaultDateRange().start)
  );
  const [endDate, setEndDate] = useState<Date>(
    () => (searchParams.get("end") ? new Date(searchParams.get("end") as string) : getDefaultDateRange().end)
  );
  const [carrierFilter, setCarrierFilter] = useState(searchParams.get("carrier") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
  const [destinationFilter, setDestinationFilter] = useState(searchParams.get("destination") || "");
  // Account deep link (e.g. from Wholesale Clients "View CDRs")
  const [accountFilter, setAccountFilter] = useState(searchParams.get("account") || "");
  const [page, setPage] = useState(() => parseInt(searchParams.get("page") || "1", 10));
  const [pageSize, setPageSize] = useState(() => parseInt(searchParams.get("size") || "100", 10));

  // D2: server-side sorting, mirrored to the URL
  const [sorting, setSorting] = useState<SortingState>(() => {
    const col = searchParams.get("sort");
    const dir = searchParams.get("dir");
    return col ? [{ id: col, desc: dir !== "asc" }] : [{ id: "start_time", desc: true }];
  });

  // Row selection state
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  // CDR detail dialog (row click)
  const [detailCallId, setDetailCallId] = useState<string | null>(null);

  // Saved views (D3)
  const [savedViews, setSavedViews] = useState<SavedView[]>(loadSavedViews);
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [newViewName, setNewViewName] = useState("");

  // Suppliers for carrier filter
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);

  /** Mirror the current filter state into the URL (A4). */
  const syncUrl = useCallback(
    (overrides?: {
      page?: number;
      sorting?: SortingState;
      start?: Date;
      end?: Date;
      carrier?: string;
      status?: string;
      destination?: string;
      account?: string;
      preset?: string;
    }) => {
      const o = overrides ?? {};
      const next = new URLSearchParams();
      const s = o.start ?? startDate;
      const e = o.end ?? endDate;
      const c = o.carrier ?? carrierFilter;
      const st = o.status ?? statusFilter;
      const d = o.destination ?? destinationFilter;
      const acct = o.account ?? accountFilter;
      const p = o.page ?? page;
      const sort = o.sorting ?? sorting;

      if (s) next.set("start", s.toISOString());
      if (e) next.set("end", e.toISOString());
      if (c) next.set("carrier", c);
      if (st) next.set("status", st);
      if (d) next.set("destination", d);
      if (acct) next.set("account", acct);
      if (p > 1) next.set("page", String(p));
      if (pageSize !== 100) next.set("size", String(pageSize));
      if (sort[0]) {
        next.set("sort", sort[0].id);
        next.set("dir", sort[0].desc ? "desc" : "asc");
      }
      const preset = o.preset ?? datePreset;
      if (preset) next.set("preset", preset);

      setSearchParams(next, { replace: true });
    },
    [startDate, endDate, carrierFilter, statusFilter, destinationFilter, accountFilter, page, pageSize, sorting, datePreset, setSearchParams]
  );

  /** Fetch CDRs from API (D2: sorting is applied server-side). */
  const fetchCdrs = useCallback(
    async (overrides?: { page?: number; sorting?: SortingState }) => {
      const p = overrides?.page ?? page;
      const sort = overrides?.sorting ?? sorting;
      try {
        setLoading(true);

        const params: {
          start_date: string;
          end_date: string;
          carrier_id?: string;
          status?: string;
          destination?: string;
          account_filter?: string;
          page: number;
          page_size: number;
          sort_by?: string;
          sort_order?: string;
        } = {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          page: p,
          page_size: pageSize,
          sort_by: sort[0]?.id ?? "start_time",
          sort_order: sort[0]?.desc === false ? "asc" : "desc",
        };

        if (carrierFilter) params.carrier_id = carrierFilter;
        if (statusFilter) params.status = statusFilter;
        if (destinationFilter) params.destination = destinationFilter;
        if (accountFilter) params.account_filter = accountFilter;

        const response = await cdrsApi.list(params);
        const data: CdrListResponse = response.data;

        setCdrs(data.cdrs);
        setTotal(data.total);
      } catch (err: any) {
        notify(err.response?.data?.detail || "Failed to load CDRs", "error");
      } finally {
        setLoading(false);
      }
    },
    [startDate, endDate, carrierFilter, statusFilter, destinationFilter, accountFilter, page, pageSize, sorting, notify]
  );

  /** Fetch suppliers for carrier filter. */
  const fetchSuppliers = useCallback(async () => {
    setSuppliersLoading(true);
    try {
      const response = await suppliersApi.list({ page: 1, size: 100 });
      setSuppliers(response.data.items || []);
    } catch {
      // Filter stays empty; the CDR fetch surfaces its own errors
    } finally {
      setSuppliersLoading(false);
    }
  }, []);

  /** Fetch suppliers on mount. */
  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  /** Fetch CDRs when query parameters change. */
  useEffect(() => {
    fetchCdrs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, carrierFilter, statusFilter, destinationFilter, accountFilter, page, pageSize, sorting]);

  /** Determine empty state type. */
  const getEmptyStateType = (): "first_time" | "no_results" => {
    const hasFilters = carrierFilter || statusFilter || destinationFilter;
    return hasFilters ? "no_results" : "first_time";
  };

  /** Clear all filters. */
  const clearFilters = () => {
    setCarrierFilter("");
    setStatusFilter("");
    setDestinationFilter("");
    setAccountFilter("");
    setDatePreset("today");
    const defaultRange = getDefaultDateRange();
    setStartDate(defaultRange.start);
    setEndDate(defaultRange.end);
    setPage(1);
    const nextSorting: SortingState = [{ id: "start_time", desc: true }];
    setSorting(nextSorting);
    syncUrl({
      page: 1,
      sorting: nextSorting,
      start: defaultRange.start,
      end: defaultRange.end,
      carrier: "",
      status: "",
      destination: "",
      account: "",
      preset: "today",
    });
  };

  /** Handle date preset change. */
  const handleDatePresetChange = useCallback(
    (period: string, start: Date, end: Date) => {
      setDatePreset(period);
      setStartDate(start);
      setEndDate(end);
      setPage(1);
      syncUrl({ preset: period, start, end, page: 1 });
    },
    [syncUrl]
  );

  /** Handle date range change. */
  const handleDateChange = (start: Date, end: Date) => {
    setDatePreset(""); // Clear preset when using custom date
    setStartDate(start);
    setEndDate(end);
    setPage(1);
    syncUrl({ preset: "", start, end, page: 1 });
  };

  /** Handle page change. */
  const handlePageChange = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
    }
  };

  /** Handle server-side sorting change (D2). */
  const handleSortingChange = useCallback(
    (next: SortingState) => {
      setSorting(next);
      setPage(1);
      syncUrl({ sorting: next, page: 1 });
    },
    [syncUrl]
  );

  /** Handle selection change from CdrBrowser. */
  const handleSelectionChange = (selectedIds: string[]) => {
    setSelectedRows(selectedIds);
  };

  /** Handle destination filter change. */
  const handleDestinationChange = (destination: string) => {
    setDestinationFilter(destination);
    setPage(1);
    syncUrl({ destination, page: 1 });
  };

  /** Get export query parameters. */
  const getExportParams = () => {
    const params: {
      start_date: string;
      end_date: string;
      carrier_id?: string;
      status?: string;
      destination?: string;
    } = {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
    };
    if (carrierFilter) params.carrier_id = carrierFilter;
    if (statusFilter) params.status = statusFilter;
    if (destinationFilter) params.destination = destinationFilter;
    return params;
  };

  /** Handle bulk export. */
  const handleBulkExport = async () => {
    try {
      const response = await cdrsApi.bulkExport(selectedRows);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `cdrs_export_${new Date().toISOString()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      notify(`Exported ${selectedRows.length} CDRs`);
    } catch (err: any) {
      notify(err.response?.data?.detail || "Bulk export failed", "error");
    }
  };

  /** Handle bulk rerate. */
  const handleBulkRerate = async () => {
    try {
      await cdrsApi.bulkRerate(selectedRows);
      notify(`Rerated ${selectedRows.length} CDRs`);
      setSelectedRows([]);
      fetchCdrs(); // Refresh data
    } catch (err: any) {
      notify(err.response?.data?.detail || "Bulk rerate failed", "error");
    }
  };

  /** Handle mark as paid. */
  const handleMarkPaid = async () => {
    try {
      await cdrsApi.bulkUpdateStatus(selectedRows, "paid");
      notify(`Marked ${selectedRows.length} CDRs as paid`);
      setSelectedRows([]);
      fetchCdrs(); // Refresh data
    } catch (err: any) {
      notify(err.response?.data?.detail || "Failed to mark as paid", "error");
    }
  };

  /** Handle mark as unpaid. */
  const handleMarkUnpaid = async () => {
    try {
      await cdrsApi.bulkUpdateStatus(selectedRows, "unpaid");
      notify(`Marked ${selectedRows.length} CDRs as unpaid`);
      setSelectedRows([]);
      fetchCdrs(); // Refresh data
    } catch (err: any) {
      notify(err.response?.data?.detail || "Failed to mark as unpaid", "error");
    }
  };

  /** Handle clear selection. */
  const handleClearSelection = () => {
    setSelectedRows([]);
  };

  /** Save the current filters as a named view (D3). */
  const handleSaveView = () => {
    const name = newViewName.trim();
    if (!name) return;
    const view: SavedView = {
      name,
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      carrier: carrierFilter,
      status: statusFilter,
      destination: destinationFilter,
    };
    // Replace any view with the same name
    const next = [...savedViews.filter((v) => v.name !== name), view].slice(-8);
    setSavedViews(next);
    localStorage.setItem(SAVED_VIEWS_KEY, JSON.stringify(next));
    setNewViewName("");
    setSaveViewOpen(false);
    notify(`View “${name}” saved`);
  };

  /** Apply a saved view. */
  const applySavedView = (view: SavedView) => {
    setStartDate(new Date(view.start));
    setEndDate(new Date(view.end));
    setCarrierFilter(view.carrier);
    setStatusFilter(view.status);
    setDestinationFilter(view.destination);
    setDatePreset("");
    setPage(1);
    syncUrl({
      start: new Date(view.start),
      end: new Date(view.end),
      carrier: view.carrier,
      status: view.status,
      destination: view.destination,
      preset: "",
      page: 1,
    });
  };

  /** Delete a saved view. */
  const deleteSavedView = (name: string) => {
    const next = savedViews.filter((v) => v.name !== name);
    setSavedViews(next);
    localStorage.setItem(SAVED_VIEWS_KEY, JSON.stringify(next));
  };

  const carrierName = useMemo(
    () => suppliers.find((s) => s.id === carrierFilter)?.name ?? carrierFilter,
    [suppliers, carrierFilter]
  );

  return (
    <Box>
      {/* Page header (G2) */}
      <PageHeader
        title="Call Detail Records"
        subtitle={`Showing ${cdrs.length} of ${total.toLocaleString()} CDRs`}
        actions={
          <>
            {savedViews.map((v) => (
              <Chip
                key={v.name}
                label={v.name}
                size="small"
                onClick={() => applySavedView(v)}
                onDelete={() => deleteSavedView(v.name)}
                deleteIcon={<DeleteViewIcon aria-label={`Delete view ${v.name}`} />}
              />
            ))}
            <Button
              size="small"
              startIcon={<SaveViewIcon />}
              onClick={() => setSaveViewOpen(true)}
            >
              Save view
            </Button>
            <ExportButton queryParams={getExportParams()} disabled={loading || total === 0} />
          </>
        }
      />

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        {/* Quick date presets */}
        <QuickDatePresets value={datePreset} onChange={handleDatePresetChange} />

        {/* Date filter */}
        <DateFilter startDate={startDate} endDate={endDate} onDateChange={handleDateChange} />

        {/* Additional filters */}
        <Box sx={{ display: "flex", gap: 2, mt: 2, flexWrap: "wrap" }}>
          {/* Carrier filter */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Carrier</InputLabel>
            <Select
              value={carrierFilter}
              label="Carrier"
              onChange={(e) => {
                setCarrierFilter(e.target.value);
                setPage(1);
                syncUrl({ carrier: e.target.value, page: 1 });
              }}
              disabled={suppliersLoading}
            >
              <MenuItem value="">All Carriers</MenuItem>
              {suppliers.map((supplier) => (
                <MenuItem key={supplier.id} value={supplier.id}>
                  {supplier.name} ({supplier.hostname})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
                syncUrl({ status: e.target.value, page: 1 });
              }}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>

          {/* Destination filter */}
          <DestinationFilter
            value={destinationFilter}
            onChange={handleDestinationChange}
            startDate={startDate.toISOString()}
            endDate={endDate.toISOString()}
          />

          {/* Active filter chips (D6: supplier name, not raw UUID) */}
          {(carrierFilter || statusFilter || destinationFilter || accountFilter) && (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              {accountFilter && (
                <Chip
                  label={`Account: ${accountFilter}`}
                  onDelete={() => {
                    setAccountFilter("");
                    setPage(1);
                    syncUrl({ account: "", page: 1 });
                  }}
                  size="small"
                  color="primary"
                />
              )}
              {carrierFilter && (
                <Chip
                  label={`Carrier: ${carrierName}`}
                  onDelete={() => {
                    setCarrierFilter("");
                    setPage(1);
                    syncUrl({ carrier: "", page: 1 });
                  }}
                  size="small"
                />
              )}
              {statusFilter && (
                <Chip
                  label={`Status: ${
                    statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)
                  }`}
                  onDelete={() => {
                    setStatusFilter("");
                    setPage(1);
                    syncUrl({ status: "", page: 1 });
                  }}
                  size="small"
                />
              )}
              {destinationFilter && (
                <Chip
                  label={`Destination: ${destinationFilter}`}
                  onDelete={() => handleDestinationChange("")}
                  size="small"
                />
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* CDR table or empty state */}
      {total === 0 && !loading ? (
        <EmptyState
          type={getEmptyStateType()}
          onClearFilters={getEmptyStateType() === "no_results" ? clearFilters : undefined}
        />
      ) : (
        <CdrBrowser
          cdrs={cdrs}
          loading={loading}
          total={total}
          page={page}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onSelectionChange={handleSelectionChange}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          onRowClick={setDetailCallId}
        />
      )}

      {/* CDR detail dialog (row click) */}
      <CdrDetailDialog
        open={detailCallId !== null}
        callId={detailCallId}
        onClose={() => setDetailCallId(null)}
      />

      {/* Bulk action bar */}
      <BulkActionBar
        selectedCount={selectedRows.length}
        onExport={handleBulkExport}
        onRerate={handleBulkRerate}
        onMarkPaid={handleMarkPaid}
        onMarkUnpaid={handleMarkUnpaid}
        onClearSelection={handleClearSelection}
      />

      {/* Save-view dialog (D3) */}
      <Dialog open={saveViewOpen} onClose={() => setSaveViewOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Save current view</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Saves the current date range and filters under a name you can re-apply
            with one click.
          </Typography>
          <TextField
            autoFocus
            label="View name"
            value={newViewName}
            onChange={(e) => setNewViewName(e.target.value)}
            fullWidth
            size="small"
            onKeyDown={(e) => e.key === "Enter" && handleSaveView()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveViewOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveView} variant="contained" disabled={!newViewName.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
